import { supabase } from './supabase';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

/**
 * Admin API Service
 * Handles all admin-related API calls through backend endpoints
 */
class AdminApiService {
  private async getAuthToken(): Promise<string> {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.access_token) {
      throw new Error('Not authenticated');
    }
    return session.access_token;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Dashboard API
  async getDashboardStats(): Promise<any> {
    return this.makeRequest('/admin/dashboard');
  }

  // Reports API
  async getReports(params: {
    page?: number;
    limit?: number;
    status?: string;
    priority?: string;
    type?: string;
    search?: string;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    return this.makeRequest(`/admin/reports${queryString ? `?${queryString}` : ''}`);
  }

  async getReportDetail(reportId: string): Promise<any> {
    return this.makeRequest(`/admin/reports?reportId=${reportId}`);
  }

  async updateReportStatus(reportId: string, status: string, reason: string): Promise<any> {
    return this.makeRequest(`/admin/reports?reportId=${reportId}`, {
      method: 'PUT',
      body: JSON.stringify({ status, reason }),
    });
  }

  // Admin Actions API
  async takeAdminAction(params: {
    reportId?: string;
    actionType: 'dismiss' | 'warn' | 'suspend' | 'ban' | 'clear_warning' | 'reinstate';
    reason: string;
    duration?: number;
    reportedUserEmail: string;
  }): Promise<any> {
    return this.makeRequest('/admin/actions', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // Clear Warning API - convenience method for clearing warnings
  async clearUserWarning(params: {
    reportedUserEmail: string;
    reason: string;
  }): Promise<any> {
    return this.takeAdminAction({
      actionType: 'clear_warning',
      reason: params.reason,
      reportedUserEmail: params.reportedUserEmail,
    });
  }

  async reinstateUser(params: {
    reportedUserEmail: string;
    reason: string;
  }): Promise<any> {
    return this.takeAdminAction({
      actionType: 'reinstate',
      reason: params.reason,
      reportedUserEmail: params.reportedUserEmail,
    });
  }

  // Users API
  async getUserProfile(email: string): Promise<any> {
    return this.makeRequest(`/admin/users?email=${encodeURIComponent(email)}`);
  }

  async getUserProfileById(userId: string): Promise<any> {
    return this.makeRequest(`/admin/users?userId=${userId}`);
  }

  async listUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    plan?: string;
    gender?: string;
    country?: string;
    verified?: string;
    warningScope?: 'all' | 'mine';
    sortBy?: 'created_at' | 'last_seen' | 'email';
    sortDir?: 'asc' | 'desc';
  } = {}): Promise<UsersListResponse> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') {
        qs.append(k, String(v));
      }
    });
    const q = qs.toString();
    return this.makeRequest(`/admin/users-list${q ? `?${q}` : ''}`);
  }

  // Check if user has active warning
  async checkUserWarningStatus(email: string): Promise<{ hasActiveWarning: boolean; currentStatus?: string }> {
    const userProfile = await this.getUserProfile(email);
    
    // Check the calculated current_status first
    if (userProfile.data?.profile?.current_status) {
      const currentStatus = userProfile.data.profile.current_status;
      const isWarned = currentStatus === 'warned';
      return {
        hasActiveWarning: isWarned,
        currentStatus: currentStatus
      };
    }
    
    // Fallback to statusHistory check
    if (!userProfile.data?.statusHistory || userProfile.data.statusHistory.length === 0) {
      return { hasActiveWarning: false };
    }

    // Get the most recent status - check both 'status' and 'new_status' fields
    const latestStatus = userProfile.data.statusHistory[0]; // Assuming it's sorted by most recent first
    const currentStatus = latestStatus.status; // Backend already maps new_status to status
    const isWarned = currentStatus === 'warned';
    
    return {
      hasActiveWarning: isWarned,
      currentStatus: currentStatus
    };
  }

  // Analytics API
  async getSignupStats(): Promise<any> {
    return this.makeRequest('/admin/analytics/signup-stats');
  }

  async getCountryDistribution(): Promise<any> {
    return this.makeRequest('/admin/analytics/country-distribution');
  }

  async getSignupTrend(params: { days: number }): Promise<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('days', params.days.toString());
    return this.makeRequest(`/admin/analytics/signup-trend?${queryParams.toString()}`);
  }

  async getTodayCountriesCount(): Promise<any> {
    return this.makeRequest('/admin/analytics/today-countries');
  }

  async getRegistrationFunnel(params: { days?: number } = {}): Promise<RegistrationFunnelResponse> {
    const qs = new URLSearchParams();
    if (params.days != null) qs.append('days', String(params.days));
    return this.makeRequest(`/admin/analytics/registration-funnel${qs.toString() ? `?${qs.toString()}` : ''}`);
  }

  async getEngagementAnalytics(params: { days?: number; limit?: number } = {}): Promise<any> {
    const qs = new URLSearchParams();
    if (params.days != null)  qs.append('days', String(params.days));
    if (params.limit != null) qs.append('limit', String(params.limit));
    return this.makeRequest(`/admin/analytics/engagement${qs.toString() ? `?${qs.toString()}` : ''}`);
  }

  async getMatchmakingAnalytics(params: { days?: number } = {}): Promise<{ success: boolean; data: MatchmakingAnalytics }> {
    const qs = new URLSearchParams();
    if (params.days != null) qs.append('days', String(params.days));
    return this.makeRequest(`/admin/analytics/matchmaking${qs.toString() ? `?${qs.toString()}` : ''}`);
  }

  async getRecentSignups(params: { limit?: number } = {}): Promise<{ success: boolean; data: RecentSignup[] }> {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.append('limit', String(params.limit));
    return this.makeRequest(`/admin/analytics/recent-signups${qs.toString() ? `?${qs.toString()}` : ''}`);
  }

  // Intro-video moderation
  async getPendingVideos(): Promise<{ pending: Array<{ userId: string; name: string; submittedAt: string; reviewUrl: string | null; moderationLabels: Array<{ name: string; confidence: number; parentName: string }> | null }> }> {
    return this.makeRequest('/admin/videos');
  }

  async moderateVideo(userId: string, action: 'approve' | 'reject'): Promise<{ ok: boolean; status: string; video_url?: string }> {
    return this.makeRequest('/admin/videos', {
      method: 'POST',
      body: JSON.stringify({ userId, action }),
    });
  }

  // Profile photo review (children and explicit content)
  async getPhotoQueue(): Promise<{ items: FlaggedPhoto[]; checked: number }> {
    return this.makeRequest('/admin/photos');
  }

  async moderatePhoto(photoUrl: string, action: 'keep' | 'remove'): Promise<{ ok: boolean; decision: string }> {
    return this.makeRequest('/admin/photos', {
      method: 'POST',
      body: JSON.stringify({ photoUrl, action }),
    });
  }
}

export interface FlaggedPhoto {
  photoUrl: string;
  userId: string;
  name: string | null;
  status: 'urgent' | 'child' | 'explicit' | 'review' | 'no_face' | 'small_face';
  youngestHigh: number | null;
  ages: string[];
  labels: Array<{ name: string; confidence: number; parentName: string }>;
  checkedAt: string;
  onProfile: boolean;
  /** The flagged photo is the member's main photo */
  isMain?: boolean;
}

// Export singleton instance
export const adminApi = new AdminApiService();

// Export types for better TypeScript support
export interface DashboardStats {
  totalReports: number;
  pendingReports: number;
  resolvedReports: number;
  dismissedReports: number;
  pendingVideos?: number;
  reportsToday: number;
  reportsThisWeek: number;
  avgResponseTimeHours: number;
  reportsByPriority: {
    urgent: number;
    high: number;
    medium: number;
    low: number;
  };
  reportsByType: Record<string, number>;
  topReportedUsers: Array<{
    user_id: string;
    report_count: number;
  }>;
  adminActivityStats: Record<string, number>;
  actionTypeStats: Record<string, number>;
  recentReports: Array<{
    id: string;
    report_type: string;
    reporter_email: string;
    reported_user_email: string;
    created_at: string;
    priority: string;
    status: string;
  }>;
  statusTrends: {
    suspensionsThisWeek: number;
    bansThisWeek: number;
  };
  systemHealth: {
    database: string;
    reportingSystem: string;
    lastUpdated: string;
  };
}

export interface RegistrationFunnelStep {
  step: number;
  label: string;
  reached: number;
  conversionFromStart: number;
  stepConversion: number;
  dropoff: number;
  atStep: number;
}

export interface RegistrationFunnelResponse {
  success: boolean;
  data: {
    days: number;
    totalEntered: number;
    completed: number;
    completionRate: number;
    funnel: RegistrationFunnelStep[];
  };
}

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  age: number | null;
  gender: string | null;
  country: string | null;
  avatar_url: string | null;
  is_active: boolean;
  is_admin: boolean;
  registration_step: number | null;
  subscription_status: string | null;
  subscription_tier: string | null;
  verification_status: string | null;
  verified_at: string | null;
  verified: boolean;
  created_at: string;
  updated_at: string | null;
  last_seen: string | null;
  status: 'active' | 'paused' | 'warned' | 'suspended' | 'banned' | 'incomplete';
  report_count: number;
  moderation_expires_at: string | null;
  warning_count: number;
  last_warning_reason: string | null;
  last_warning_at: string | null;
  last_warning_admin_id: string | null;
  last_warning_by_me: boolean;
}

export interface UsersListResponse {
  success: boolean;
  data: AdminUser[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface UserEngagement {
  keeps_made: number;
  pops_made: number;
  keeps_received: number;
  pops_received: number;
  keep_rate_received: number | null; // 0..1, null when no decisions received yet
  matches: number;
  blocks_received: number;
  blocks_made: number;
  avg_time_spent_seconds: number | null;
  avg_photos_viewed: number | null;
  bio_view_rate: number | null; // 0..1
  profile_views: number;
}

export interface RecentSignup {
  id: string;
  full_name: string | null;
  email: string | null;
  country: string;
  country_code: string | null;
  avatar_url: string | null;
  created_at: string;
  completed_onboarding: boolean | null;
}

export interface MatchmakingAnalytics {
  rangeDays: number;
  totalMatches: number;
  matchesToday: number;
  totalKeeps: number;
  totalPops: number;
  totalDecisions: number;
  keepRate: number | null; // 0..1
  trend: Array<{ date: string; matches: number }>;
}

export interface ReportDetail {
  id: string;
  report_type: string;
  reporter_email: string;
  reported_user_email: string;
  created_at: string;
  priority: string;
  status: string;
  content: string | null;
  category_description: string;
  total_reports_against_user: number;
  reporter_profile?: {
    full_name: string;
    avatar_url: string;
  };
  reported_user_profile?: {
    full_name: string;
    avatar_url: string;
    bio: string;
    age: number;
  };
  adminActions?: Array<{
    id: string;
    admin_email: string;
    action_type: string;
    reason: string;
    created_at: string;
    metadata: any;
  }>;
  engagement?: UserEngagement;
}

export interface UserProfile {
  profile: {
    id: string;
    email: string;
    full_name: string;
    bio: string;
    age: number;
    gender: string;
    location: string;
    avatar_url: string;
    photos?: Array<{ uri: string } | string>;
    created_at: string;
    updated_at: string;
    current_status: 'active' | 'warned' | 'suspended' | 'banned';
    moderation_expires_at?: string | null;
  };
  reports: Array<{
    id: string;
    report_type: string;
    reporter_email: string;
    reporter_name: string;
    created_at: string;
    status: string;
    priority: string;
    content: string;
  }>;
  statusHistory: Array<{
    id: string;
    status: string;
    reason: string;
    created_at: string;
    expires_at: string | null;
    admin_email: string;
    admin_name: string;
  }>;
  adminActions: Array<{
    id: string;
    admin_email: string;
    admin_name: string;
    action_type: string;
    reason: string;
    created_at: string;
    metadata: any;
    related_report_type?: string;
  }>;
  stats: {
    total_reports: number;
    pending_reports: number;
    resolved_reports: number;
    dismissed_reports: number;
    high_priority_reports: number;
    total_admin_actions: number;
    account_age_days: number;
  };
  engagement?: UserEngagement;
}

export interface ReportsResponse {
  success: boolean;
  data: Array<{
    id: string;
    report_type: string;
    reporter_email: string;
    reported_user_email: string;
    created_at: string;
    priority: string;
    status: string;
    content?: string;
    category_description: string;
    total_reports_against_user: number;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}
